import type { ComponentType, FC } from 'react'

import { makeVar, useReactiveVar } from '@apollo/client'
import { CenteredLoader } from 'components/common/CenteredLoader'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

/** Make sure to not set this to `false` during SSR. */
const isSSRVar = makeVar(true)

/**
 * Avoid rendering wrapped `PageComponent` during Next.js SSR
 * and replace it with a full-screen loading indicator.
 *
 * This is useful for creating client-side-only pages for Next.js, because
 * otherwise you'd need to handle the selected store and other business logic
 * server-side (see the product page, for example). A good example of a
 * client-only page is something that requires user authentication. We don't
 * want to have to deal with user info server-side.
 *
 * When you wrap the page component into this HoC and do not add any
 * `getServerSideProps` method, Next.js will compile the page into a static
 * HTML file (containing only the loading indicator) and skip all server-side logic.
 * When opening the page client-side, the UI will be rendered after loading.
 *
 * @example export default withSSRLoading(MyClientSideOnlyPage)
 */
export const withSSRLoading = (PageComponent: ComponentType) => {
  const WithSSRLoading: FC = (props) => {
    const router = useRouter()
    const isSSR = useReactiveVar(isSSRVar)

    /**
     * The Next.js router sets `router.query === undefined` on the first
     * render. Display loading indicator until the router is ready, so
     * that the page component has the full query object on its first
     * render. For more info, see:
     *
     * @see https://github.com/vercel/next.js/discussions/11484
     */
    useEffect(() => {
      if (router.isReady) {
        /** Set this to `false` inside `useEffect` to make sure it only happens client-side */
        isSSRVar(false)
      }
    }, [router.isReady])

    if (isSSR) {
      return <CenteredLoader />
    }

    return <PageComponent {...props} />
  }

  WithSSRLoading.displayName = `withSSRLoading(${
    PageComponent.displayName || PageComponent.name || 'Component'
  })`

  return WithSSRLoading
}
